import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Info, Plus, ChevronRight, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { LoginCredentials, Login2faChallenge } from '../types';
import AnimatedLink from '../components/AnimatedLink';
import CustomerNavbar from '../components/CustomerNavbar';
import EmailVerificationModal from '../components/EmailVerificationModal';
import ProfessionalHelpModal from '../components/ProfessionalHelpModal';
import Footer from '../components/Footer';
import { apiService } from '../services/api';
import { hasActiveBusinessRoles, isBusinessOwner } from '../utils/roleUtils';
import {
  clearBusiness2faTrustToken,
  getBusiness2faTrustToken,
} from '../utils/business2faTrust';
import toast from 'react-hot-toast';

const LOGIN_2FA_OTP_LENGTH = 6;
const emptyOtpDigits = () => Array(LOGIN_2FA_OTP_LENGTH).fill('') as string[];

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [emailInputElement, setEmailInputElement] = useState<HTMLInputElement | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [twoFaChallenge, setTwoFaChallenge] = useState<Login2faChallenge | null>(null);
  const [twoFaDigits, setTwoFaDigits] = useState<string[]>(emptyOtpDigits);
  const [isResending2fa, setIsResending2fa] = useState(false);
  const [rememberBrowser, setRememberBrowser] = useState(false);
  const twoFaInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const twoFaAutoSubmitRef = useRef(false);
  const rememberBrowserRef = useRef(false);

  const { login, verifyLogin2fa, resendLogin2fa, user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const twoFaCode = twoFaDigits.join('');

  // Clear error message and sessionStorage on component mount/refresh
  useEffect(() => {
    setErrorMessage(null);
    sessionStorage.removeItem('loginError');
  }, []);

  // Get redirect, login type, and showReason from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  const loginType =
    (searchParams.get('type') as 'customer' | 'business' | 'professional' | 'platform' | null) || null;
  const showReason = searchParams.get('showReason') === 'true';

  // Navigate based on login type when user is available after login
  useEffect(() => {
    if (loginSuccess && user) {
      // Login type is already stored in localStorage during login
      
      // If a login type is specified, override redirect behavior
      if (loginType === 'platform') {
        if (redirectUrl) {
          navigate(redirectUrl);
        } else {
          navigate('/dashboard/platform');
        }
        setLoginSuccess(false);
        return;
      }
      if (loginType === 'business') {
        const canAccessBusiness = hasActiveBusinessRoles(user);
        if (!canAccessBusiness) {
          const message = 'Business sign in requires a business account. Your account is not associated with any business.';
          setErrorMessage(message);
          sessionStorage.setItem('loginError', message);
          setLoginSuccess(false);
          return;
        }
        navigate('/dashboard/business?section=appointments');
      } else if (loginType === 'professional') {
        // Professional login: check if user has business roles, if so go to business dashboard, else customer dashboard
        const canAccessBusiness = hasActiveBusinessRoles(user);
        if (canAccessBusiness) {
          const targetSection = isBusinessOwner(user) ? 'appointments' : 'my-appointments';
          navigate(`/dashboard/business?section=${targetSection}`);
        } else {
          // If no business access, go to customer dashboard or search
          if (redirectUrl) {
            navigate(redirectUrl);
          } else {
            navigate('/search');
          }
        }
      } else {
        // Customer or unspecified type: allow optional redirect, else go to customer dashboard
        if (redirectUrl) {
          navigate(redirectUrl);
        } else {
          navigate('/dashboard/customer');
        }
      }
      setLoginSuccess(false);
    }
  }, [loginSuccess, user, navigate, redirectUrl, loginType]);

  // Clear error from sessionStorage when component unmounts or error is cleared
  useEffect(() => {
    if (!errorMessage) {
      sessionStorage.removeItem('loginError');
    }
  }, [errorMessage]);

  // Focus email input on page load or after failed login
  useEffect(() => {
    if (emailInputElement && !twoFaChallenge) {
      // Small delay to ensure the input is fully rendered
      const timer = setTimeout(() => {
        emailInputElement?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, emailInputElement, twoFaChallenge]); // Re-focus when error message changes (after failed login)

  // Keep ref in sync so OTP auto-submit uses the latest checkbox value
  useEffect(() => {
    rememberBrowserRef.current = rememberBrowser;
  }, [rememberBrowser]);

  // Focus first OTP digit when the 2FA challenge appears (or is resent)
  useEffect(() => {
    if (!twoFaChallenge) return;
    twoFaAutoSubmitRef.current = false;
    setRememberBrowser(false);
    rememberBrowserRef.current = false;
    const timer = setTimeout(() => {
      twoFaInputRefs.current[0]?.focus();
      twoFaInputRefs.current[0]?.select();
    }, 50);
    return () => clearTimeout(timer);
  }, [twoFaChallenge?.challengeId]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginCredentials>();

  // Register email field with custom ref
  const emailRegistration = register('email', {
    required: 'Email is required',
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: 'Invalid email address',
    },
    onChange: (e) => {
      const value = e.target.value.toLowerCase();
      e.target.value = value;
      setValue('email', value, { shouldValidate: false });
    },
  });

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      const result = await apiService.resendVerification(verificationEmail);
      if (result.success) {
        toast.success('Verification email sent successfully! Please check your inbox.');
        setShowVerificationModal(false);
        navigate(`/account-verification-sent?email=${encodeURIComponent(verificationEmail)}&type=${encodeURIComponent(loginType || 'customer')}`);
      } else {
        toast.error(result.message || 'Failed to send verification email');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to send verification email';
      toast.error(errorMsg);
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    setVerificationEmail('');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const completeSuccessfulLogin = () => {
    if (loginType) {
      localStorage.setItem('loginType', loginType);
    }
    toast.success('Login successful!');
    setTwoFaChallenge(null);
    setTwoFaDigits(emptyOtpDigits());
    setRememberBrowser(false);
    rememberBrowserRef.current = false;
    setLoginSuccess(true);
  };

  const onSubmit = async (data: LoginCredentials, e?: React.BaseSyntheticEvent) => {
    if (e) {
      e.preventDefault();
    }
    setIsLoading(true);
    setErrorMessage(null); // Clear any previous errors
    sessionStorage.removeItem('loginError'); // Clear from sessionStorage too
    try {
      const normalizedEmail = data.email.trim().toLowerCase();
      const trustToken =
        loginType === 'business' ? getBusiness2faTrustToken(normalizedEmail) || undefined : undefined;
      // Include login type in credentials
      const result = await login({
        ...data,
        email: normalizedEmail,
        loginType: loginType || undefined,
        trustToken,
      });
      if (result?.requires2fa) {
        // Stale/invalid trust token — clear so a future remember can replace it
        clearBusiness2faTrustToken(normalizedEmail);
        setTwoFaChallenge(result);
        setTwoFaDigits(emptyOtpDigits());
        toast.success('Verification code sent to your email');
        return;
      }
      completeSuccessfulLogin();
    } catch (error: any) {
      let errorMsg: string;
      if (!error.response && (error.request || error.message?.toLowerCase().includes('network'))) {
        errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        errorMsg = error.response?.data?.message || 'Login failed. Please try again.';
      }

      if (errorMsg.toLowerCase().includes('verify your email') ||
          errorMsg.toLowerCase().includes('email has not been verified') ||
          errorMsg.toLowerCase().includes('email address before logging in')) {
        setVerificationEmail(data.email.trim().toLowerCase());
        setShowVerificationModal(true);
        setErrorMessage(null);
        sessionStorage.removeItem('loginError');
      } else {
        setErrorMessage(errorMsg);
        sessionStorage.setItem('loginError', errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const submitLogin2faCode = async (code: string) => {
    if (!twoFaChallenge) return;
    if (!/^\d{6}$/.test(code)) {
      setErrorMessage('Enter the 6-digit verification code sent to your email');
      return;
    }
    if (twoFaAutoSubmitRef.current || isLoading) return;

    twoFaAutoSubmitRef.current = true;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await verifyLogin2fa(twoFaChallenge.challengeId, code, rememberBrowserRef.current);
      completeSuccessfulLogin();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        (!error.response && (error.request || error.message?.toLowerCase().includes('network'))
          ? 'Unable to connect to the server. Please check your internet connection and try again.'
          : 'Verification failed. Please try again.');
      setErrorMessage(errorMsg);
      setTwoFaDigits(emptyOtpDigits());
      twoFaAutoSubmitRef.current = false;
      // Session invalidated after too many attempts — return to password step
      if (
        errorMsg.toLowerCase().includes('sign in again') ||
        errorMsg.toLowerCase().includes('expired')
      ) {
        setTwoFaChallenge(null);
      } else {
        requestAnimationFrame(() => {
          twoFaInputRefs.current[0]?.focus();
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onVerify2fa = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    await submitLogin2faCode(twoFaCode);
  };

  const focusOtpIndex = (index: number) => {
    const clamped = Math.max(0, Math.min(LOGIN_2FA_OTP_LENGTH - 1, index));
    requestAnimationFrame(() => {
      const el = twoFaInputRefs.current[clamped];
      el?.focus();
      el?.select();
    });
  };

  const applyOtpDigitsFromIndex = (startIndex: number, incomingDigits: string) => {
    const cleaned = incomingDigits.replace(/\D/g, '');
    if (!cleaned) return;

    setTwoFaDigits((prev) => {
      const next = [...prev];
      const chars = cleaned.slice(0, LOGIN_2FA_OTP_LENGTH - startIndex).split('');
      chars.forEach((digit, offset) => {
        next[startIndex + offset] = digit;
      });
      const nextFocus = Math.min(startIndex + chars.length, LOGIN_2FA_OTP_LENGTH - 1);
      focusOtpIndex(nextFocus);
      return next;
    });
    if (errorMessage) setErrorMessage(null);
  };

  const handleOtpDigitChange = (index: number, value: string) => {
    // Some browsers fire change with the full pasted string in one field
    if (value.length > 1) {
      applyOtpDigitsFromIndex(index, value);
      return;
    }

    const digit = value.replace(/\D/g, '').slice(-1);
    setTwoFaDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      if (digit && index < LOGIN_2FA_OTP_LENGTH - 1) {
        focusOtpIndex(index + 1);
      }
      return next;
    });
    if (errorMessage) setErrorMessage(null);
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setTwoFaDigits((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      if (index > 0) {
        focusOtpIndex(index - 1);
      }
      if (errorMessage) setErrorMessage(null);
      return;
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusOtpIndex(index - 1);
      return;
    }

    if (e.key === 'ArrowRight' && index < LOGIN_2FA_OTP_LENGTH - 1) {
      e.preventDefault();
      focusOtpIndex(index + 1);
      return;
    }

    // Replace current digit when typing another number while focused
    if (/^\d$/.test(e.key) && twoFaDigits[index]) {
      e.preventDefault();
      handleOtpDigitChange(index, e.key);
    }
  };

  const handleOtpPaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    applyOtpDigitsFromIndex(index, e.clipboardData.getData('text') || '');
  };

  const onResend2fa = async () => {
    if (!twoFaChallenge || isResending2fa) return;
    setIsResending2fa(true);
    setErrorMessage(null);
    try {
      const nextChallenge = await resendLogin2fa(twoFaChallenge.challengeId);
      setTwoFaChallenge(nextChallenge);
      setTwoFaDigits(emptyOtpDigits());
      twoFaAutoSubmitRef.current = false;
      toast.success('A new verification code has been sent');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to resend verification code';
      setErrorMessage(errorMsg);
      if (errorMsg.toLowerCase().includes('sign in again') || errorMsg.toLowerCase().includes('expired')) {
        setTwoFaChallenge(null);
        setTwoFaDigits(emptyOtpDigits());
      }
    } finally {
      setIsResending2fa(false);
    }
  };

  const handleBackFrom2fa = () => {
    setTwoFaChallenge(null);
    setTwoFaDigits(emptyOtpDigits());
    setRememberBrowser(false);
    rememberBrowserRef.current = false;
    twoFaAutoSubmitRef.current = false;
    setErrorMessage(null);
  };

  const handleGoogleSignIn = useCallback(async (response: any) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      // Extract the credential string from the response
      // Google Identity Services returns an object with a credential property
      // The credential property contains the JWT token string
      let credential: string;
      
      if (typeof response === 'string') {
        // If it's already a string, use it directly
        credential = response;
      } else if (response?.credential) {
        // If it's an object with credential property
        credential = typeof response.credential === 'string' 
          ? response.credential 
          : response.credential.credential;
      } else {
        throw new Error('No credential received from Google');
      }
      
      if (!credential || typeof credential !== 'string') {
        throw new Error('Invalid credential format from Google');
      }

      const authResponse = await apiService.googleLogin(credential, loginType || undefined);
      // Store token and user
      localStorage.setItem('authToken', authResponse.token);
      localStorage.setItem('user', JSON.stringify(authResponse.user));
      // Store login type immediately after successful login
      if (loginType) {
        localStorage.setItem('loginType', loginType);
      }
      // Update auth context user state (important for navigation)
      updateUser(authResponse.user);
      toast.success('Login successful!');
      setLoginSuccess(true);
    } catch (error: any) {
      console.error('Google login error:', error);
      
      // Check if this is a network error (no response from server)
      let errorMsg: string;
      if (!error.response && (error.request || error.message?.toLowerCase().includes('network'))) {
        // Network error - no response received from server
        errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        // Server responded with an error or other error
        errorMsg = error.response?.data?.message || error.message || 'Google login failed. Please try again.';
      }
      
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [loginType, updateUser]);

  // Initialize Google Sign-In when component mounts
  const googleSignInRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (loginType === 'customer' && (window as any).google && googleSignInRef.current) {
        (window as any).google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '946690405620-31eg6dhvpvf2mud0t67dtdmo6bjbkpif.apps.googleusercontent.com',
          callback: handleGoogleSignIn,
        });

        (window as any).google.accounts.id.renderButton(
          googleSignInRef.current,
          {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            width: '100%'
          }
        );
      }
    };

    // Wait for Google script to load
    if ((window as any).google) {
      initializeGoogleSignIn();
    } else {
      // Check periodically if Google script has loaded
      const checkInterval = setInterval(() => {
        if ((window as any).google) {
          initializeGoogleSignIn();
          clearInterval(checkInterval);
        }
      }, 100);

      // Cleanup after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
    }

    return () => {
      // Cleanup function
    };
  }, [loginType, handleGoogleSignIn]);



  return (
    <div className="min-h-screen bg-white flex flex-col no-animations">
      {/* Customer Navbar */}
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={false}
        showLogoutButton={false}
        showAuthButtons={true}
      />
      
      {/* Main Content - Vertically Centered */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
              {twoFaChallenge ? (
                <ShieldCheck className="h-10 w-10 text-blue-900" strokeWidth={2} />
              ) : (
                <Lock className="h-10 w-10 text-blue-900" strokeWidth={2} />
              )}
            </div>
          </div>
           <h2 className="text-3xl font-bold text-gray-900">
            {twoFaChallenge
              ? 'Verify your identity'
              : loginType === 'business'
              ? 'Business Sign In'
              : loginType === 'professional'
                ? 'Professional Login'
                : loginType === 'platform'
                  ? 'Platform Administrator Sign In'
                  : 'Customer Sign In'}
           </h2>
          <p className="mt-2 text-sm text-gray-600">
            {twoFaChallenge
              ? `Enter the 6-digit code sent to ${twoFaChallenge.email}`
              : loginType === 'business'
              ? 'Sign in to manage your business'
              : loginType === 'professional'
                ? 'Sign in to manage your schedule and accept bookings'
                : loginType === 'platform'
                  ? 'Restricted access. Only AACore platform administrator accounts can sign in here.'
                  : 'Sign in to your account to continue'}
          </p>
        </div>

        {/* Login / 2FA Form */}
        <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-sm">
          {twoFaChallenge ? (
            <form className="space-y-6" onSubmit={onVerify2fa} noValidate>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3 items-start">
                  <Mail className="h-5 w-5 text-blue-700 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-900 leading-relaxed">
                    Because online payments are enabled for your business, we sent a verification code to your email. The code expires in 10 minutes.
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="twoFaCode-0" className="block text-sm font-medium text-gray-700 mb-2">
                  Verification code
                </label>
                <div
                  className="flex items-center justify-between gap-2 sm:gap-3"
                  role="group"
                  aria-label="6-digit verification code"
                >
                  {twoFaDigits.map((digit, index) => (
                    <input
                      key={index}
                      id={`twoFaCode-${index}`}
                      ref={(el) => {
                        twoFaInputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      maxLength={1}
                      value={digit}
                      disabled={isLoading}
                      aria-label={`Digit ${index + 1} of ${LOGIN_2FA_OTP_LENGTH}`}
                      onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={(e) => handleOtpPaste(index, e)}
                      onFocus={(e) => e.target.select()}
                      className={`w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-semibold rounded-lg border bg-white text-gray-900 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-60 disabled:cursor-not-allowed ${
                        errorMessage
                          ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                          : 'border-gray-300 focus:border-blue-900 focus:ring-blue-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberBrowser}
                  disabled={isLoading}
                  onChange={(e) => setRememberBrowser(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-900"
                />
                <span className="text-sm text-gray-700 leading-relaxed">
                  Remember this browser for 14 days
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Skip email verification on this browser until then. Other browsers still require a code.
                  </span>
                </span>
              </label>

              <button
                type="submit"
                disabled={isLoading || twoFaCode.length !== 6}
                className="aacore-btn-primary w-full justify-center focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" />
                    Verify and sign in
                  </span>
                )}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleBackFrom2fa}
                  className="inline-flex items-center gap-1 font-medium text-gray-600 hover:text-gray-900 bg-transparent border-0 p-0 cursor-pointer"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={onResend2fa}
                  disabled={isResending2fa || isLoading}
                  className="font-medium text-blue-900 hover:text-blue-800 bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50"
                >
                  {isResending2fa ? 'Sending...' : 'Resend code'}
                </button>
              </div>
            </form>
          ) : (
          <form
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)(e);
            }}
            noValidate
          >
            {/* Why Sign In Section - Only shown when type=customer and showReason=true */}
            {loginType === 'customer' && showReason && (
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                      <Info className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      Why do I need to sign in?
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Signing in allows us to securely manage your appointments, send you confirmations, and provide you with the best service experience.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`aacore-textfield-primary ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your email"
                  {...emailRegistration}
                  ref={(e) => {
                    setEmailInputElement(e);
                    emailRegistration.ref(e);
                  }}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`aacore-textfield-primary pr-10 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link
                  to={
                    loginType
                      ? `/forgot-password?type=${encodeURIComponent(loginType)}`
                      : '/forgot-password'
                  }
                  className="font-medium text-blue-900 hover:text-blue-800"
                >
                  Forgot your password?
                </Link>
              </div>
              {loginType === 'professional' && (
                <div className="text-sm">
                  <button
                    type="button"
                    onClick={() => setShowHelpModal(true)}
                    className="font-medium text-blue-900 hover:text-blue-800 bg-transparent border-0 p-0 cursor-pointer"
                  >
                    Help
                  </button>
                </div>
              )}
            </div>

            {/* Error Alert */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              onClick={(e) => {
                if (isLoading) {
                  e.preventDefault();
                }
              }}
              className="aacore-btn-primary w-full justify-center focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <i className="bi bi-box-arrow-in-right" style={{ fontSize: '1rem' }}></i>
                  Sign in
                </span>
              )}
            </button>
          </form>
          )}

          {/* Register Link */}
          {!twoFaChallenge && loginType !== 'professional' && loginType !== 'platform' && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <AnimatedLink
                  to={loginType === 'business' ? '/business-registration/' : '/register?type=customer'}
                  className="font-medium text-blue-900 hover:text-blue-800"
                >
                  Sign up here
                </AnimatedLink>
              </p>
            </div>
          )}

          {/* Google Sign In Button - Only for customer login */}
          {!twoFaChallenge && loginType === 'customer' && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
              <div 
                ref={googleSignInRef}
                className="mt-4 w-full flex justify-center"
                style={{ minHeight: '40px' }}
              ></div>
            </div>
          )}
        </div>

        {/* Professional Info Card */}
        {!twoFaChallenge && loginType === 'professional' && (
          <div className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm mt-4">
            <div className="flex gap-3 items-start">
              <div className="hidden md:flex flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Info className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-2">
                  <strong className="text-gray-900">Need an invitation?</strong>{' '}
                  <span className="text-gray-700">Professional access is provided by the business you work with.</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Professionals can't self-register because the account must be securely linked to a specific business workspace and permissions.
                </p>
                <div className="flex flex-wrap gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setShowHelpModal(true)}
                    className="aacore-btn-outlined inline-flex items-center gap-2 w-full md:w-auto justify-center"
                  >
                    <ChevronRight className="h-4 w-4" />
                    View details
                  </button>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <strong className="text-gray-900 mb-3 block">Work for yourself?</strong>
                  <Link
                    to="/business-registration/"
                    className="aacore-btn-outlined inline-flex items-center gap-2 w-full md:w-auto justify-center"
                  >
                    <Plus className="h-4 w-4" />
                    Create business account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={handleCloseVerificationModal}
        onResendVerification={handleResendVerification}
        email={verificationEmail}
        isEmailKnownToExist
        isLoading={isResendingVerification}
      />

      {/* Professional Help Modal */}
      <ProfessionalHelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LoginPage;
